<script setup lang="ts">
  import { ref, computed, onMounted } from "vue";
  import { storeToRefs } from "pinia";
  import { Pencil, Trash2 } from "lucide-vue-next";
  import { useWebhooksStore } from "@stores/webhooks";
  import { useToastsStore } from "@stores/toastsStore";

  import { IndexPageLayout, IndexPageFilters } from "@/components/App/IndexPage";
  import {
    DataTable,
    DataTableActions,
    type DataTableColumn,
    type DataTableAction,
  } from "@/components/episto-ui/data-table";
  import { Badge } from "@/components/ui/badge";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import WebhookFormDialog, { type WebhookForm } from "./WebhookFormDialog.vue";
  import { webhookTriggerLabel } from "@utils/constants/webhookEvents";
  import type { Webhook } from "@/types";

  const webhooksStore = useWebhooksStore();
  const toast = useToastsStore();
  const { webhooks } = storeToRefs(webhooksStore);

  const searchQuery = ref("");

  const filteredWebhooks = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return webhooks.value;
    return webhooks.value.filter(
      (webhook) => webhook.name.toLowerCase().includes(query) || webhook.url.toLowerCase().includes(query),
    );
  });

  const emptyForm = (): WebhookForm => ({
    name: "",
    url: "",
    event: "survey_status_changed",
    target_status: null,
    delay_days: 0,
    kind: "claude_routine",
    token: "",
    headers: "",
    payload: "",
    active: true,
  });

  const isDialogOpen = ref(false);
  const isEditing = ref(false);
  const currentWebhook = ref<WebhookForm>(emptyForm());

  onMounted(() => webhooksStore.fetchWebhooks());

  const columns: DataTableColumn<Webhook>[] = [
    { key: "name", label: "Name", class: "font-medium" },
    { key: "url", label: "URL", class: "font-mono text-xs max-w-md truncate" },
    { key: "event", label: "Trigger" },
    { key: "kind", label: "Type" },
    { key: "active", label: "Active" },
  ];

  const tableActions: DataTableAction[] = [
    { key: "edit", label: "Edit", icon: Pencil },
    { key: "delete", label: "Delete", icon: Trash2, variant: "destructive", separatorBefore: true },
  ];

  const openCreateDialog = () => {
    isEditing.value = false;
    currentWebhook.value = emptyForm();
    isDialogOpen.value = true;
  };

  const openEditDialog = (webhook: Webhook) => {
    isEditing.value = true;
    currentWebhook.value = {
      id: webhook.id,
      name: webhook.name,
      url: webhook.url,
      event: webhook.event,
      target_status: webhook.target_status,
      delay_days: webhook.delay_days,
      kind: webhook.kind,
      token: webhook.token ?? "",
      headers: Object.keys(webhook.headers ?? {}).length ? JSON.stringify(webhook.headers, null, 2) : "",
      payload: Object.keys(webhook.payload ?? {}).length ? JSON.stringify(webhook.payload, null, 2) : "",
      active: webhook.active,
    };
    isDialogOpen.value = true;
  };

  const parseJsonField = (value: string): Record<string, unknown> => (value.trim() ? JSON.parse(value) : {});

  const saveWebhook = async () => {
    try {
      const body = {
        name: currentWebhook.value.name,
        url: currentWebhook.value.url,
        event: currentWebhook.value.event,
        target_status:
          currentWebhook.value.event === "survey_status_changed" ? currentWebhook.value.target_status : null,
        delay_days: Number(currentWebhook.value.delay_days) || 0,
        kind: currentWebhook.value.kind,
        token: currentWebhook.value.token,
        headers: parseJsonField(currentWebhook.value.headers) as Record<string, string>,
        payload: parseJsonField(currentWebhook.value.payload),
        active: currentWebhook.value.active,
      };

      if (isEditing.value && currentWebhook.value.id) {
        await webhooksStore.updateWebhook(currentWebhook.value.id, body);
        toast.success("Hook updated successfully");
      } else {
        await webhooksStore.createWebhook(body);
        toast.success("Hook created successfully");
      }
      await webhooksStore.fetchWebhooks();
      isDialogOpen.value = false;
    } catch (error) {
      console.error(error);
      toast.error("An error occurred");
    }
  };

  const {
    isOpen: isDeleteAlertOpen,
    target: webhookToDelete,
    requestDelete: confirmDelete,
    confirm: handleDelete,
  } = useConfirmDelete<Webhook>(async (webhook) => {
    try {
      await webhooksStore.deleteWebhook(webhook.id);
      toast.success("Hook deleted successfully");
    } catch {
      toast.error("Failed to delete hook");
    }
  });

  const handleAction = (action: string, webhook: Webhook) => {
    if (action === "edit") {
      openEditDialog(webhook);
    } else if (action === "delete") {
      confirmDelete(webhook);
    }
  };
</script>

<template>
  <IndexPageLayout
    title="Hooks"
    action-label="Add Hook"
    @action="openCreateDialog">
    <template #filters>
      <IndexPageFilters
        v-model:search-model-value="searchQuery"
        search-placeholder="Search hooks..." />
    </template>

    <DataTable
      :items="filteredWebhooks"
      :columns="columns"
      empty-message="No hooks found.">
      <template #cell-event="{ item }">
        {{ webhookTriggerLabel(item) }}
      </template>
      <template #cell-kind="{ item }">
        <Badge :variant="item.kind === 'claude_routine' ? 'default' : 'secondary'">
          {{ item.kind === "claude_routine" ? "Claude routine" : "Custom" }}
        </Badge>
      </template>
      <template #cell-active="{ item }">
        <Badge :variant="item.active ? 'success' : 'secondary'">
          {{ item.active ? "Active" : "Inactive" }}
        </Badge>
      </template>
      <template #actions="{ item }">
        <DataTableActions
          :actions="tableActions"
          @action="(action) => handleAction(action, item)" />
      </template>
    </DataTable>

    <template #dialogs>
      <WebhookFormDialog
        v-model:webhook="currentWebhook"
        :open="isDialogOpen"
        :is-editing="isEditing"
        @update:open="isDialogOpen = $event"
        @save="saveWebhook" />

      <ConfirmDialog
        v-model:open="isDeleteAlertOpen"
        title="Are you absolutely sure?"
        confirm-label="Delete"
        @confirm="handleDelete">
        This action cannot be undone. This will permanently delete the hook
        <span class="font-bold">{{ webhookToDelete?.name }}</span>
        and remove all associated data from our servers.
      </ConfirmDialog>
    </template>
  </IndexPageLayout>
</template>
